import { convertToMetres } from '../../../reducers/helpers'
import type { UpdatePlayerPayload } from '../../../services/player/playerProfile'
import { GENDER_UNSPECIFIED } from './constants'
import type { EditPlayerFormValues } from './types'

/**
 * Form string field → BE snake_case key. Image/height/gender handled separately.
 * Phone numbers are plain strings here — we store/send the full E.164 number
 * (which already encodes the country); no separate `_country` field is sent.
 */
const STRING_FIELD_MAP: Record<string, keyof UpdatePlayerPayload> = {
  firstName: 'name_first',
  lastName: 'name_last',
  birthDate: 'birth_date',
  email: 'email',
  phoneNumber: 'phone_number',
  alternatePhoneNumber: 'alternate_phone_number',
  preferredContactMethod: 'preferred_contact_method',
  preferredLanguage: 'preferred_language',
  emergencyContactName: 'emergency_contact_name',
  emergencyContactRelationship: 'emergency_contact_relationship',
  emergencyContactPhone: 'emergency_contact_phone_number',
  instagram: 'instagram',
  facebook: 'facebook',
  tiktok: 'tiktok',
  youtube: 'youtube',
  leagueRegistrationNumber: 'league_registration_number',
  hockeyUsaNumber: 'hockey_usa_number',
  hockeyCanadaNumber: 'hockey_canada_number',
  customMembershipNumber: 'custom_membership_number',
  loyaltyNumber: 'loyalty_number',
}

const trim = (v: string): string => (v ?? '').trim()

/** Parse a numeric-id string to int, or null when empty/non-numeric. */
const toIntOrNull = (v: string): number | null => {
  const s = trim(v)
  if (s === '') {
    return null
  }
  const n = parseInt(s, 10)
  return Number.isNaN(n) ? null : n
}

/** Address sub-fields, diffed together; a change to any sends the full object. */
const ADDRESS_FIELDS: ReadonlyArray<keyof EditPlayerFormValues> = [
  'addressStreet1',
  'addressStreet2',
  'addressCity',
  'addressPostalCode',
  'addressCountryId',
  'addressProvinceId',
]

/**
 * Diff `current` against `initial` and return a partial PUT body with ONLY the
 * changed fields. String fields are trimmed before comparison; a cleared field
 * is sent as an explicit empty string. Height (ft/in) collapses to a single
 * metric `height` number and is included only if ft or in changed; a blank side
 * counts as 0, and both blank omits height. Weight is an int. Image-only form
 * fields are never emitted (handled by the image step).
 */
export const toUpdatePayload = (
  current: EditPlayerFormValues,
  initial: EditPlayerFormValues
): UpdatePlayerPayload => {
  const payload: UpdatePlayerPayload = {}

  // Plain string fields
  ;(
    Object.keys(STRING_FIELD_MAP) as Array<keyof typeof STRING_FIELD_MAP>
  ).forEach((formKey) => {
    const beKey = STRING_FIELD_MAP[formKey]
    const cur = trim(current[formKey] as string)
    const init = trim(initial[formKey] as string)
    if (cur !== init) {
      // @ts-expect-error indexed assignment across the union is safe here
      payload[beKey] = cur
    }
  })

  // Height: combine ft/in → metres; include only if ft or in changed.
  if (
    trim(current.heightFt) !== trim(initial.heightFt) ||
    trim(current.heightIn) !== trim(initial.heightIn)
  ) {
    const ft = trim(current.heightFt)
    const inches = trim(current.heightIn)
    // A one-sided entry treats the blank side as 0 (6ft + blank → 6'0"), so the
    // user doesn't have to type a redundant 0. Both blank is a cleared height and
    // stays omitted: the BE has no "clear height" via this form path (TODO:
    // confirm null-to-clear). convertToMetres rejects '' outright, so normalize
    // the missing side to '0' before converting.
    if (ft !== '' || inches !== '') {
      const metres = convertToMetres({
        ft: ft === '' ? '0' : ft,
        inches: inches === '' ? '0' : inches,
      })
      if (metres !== false && !Number.isNaN(metres)) {
        payload.height = metres
      }
    }
  }

  // Weight: integer lbs. A cleared weight is omitted (not sent) — same stance as
  // height: the BE has no confirmed "clear to null" path for this form yet.
  // TODO: if the BE accepts weight: null to clear, emit it when w === ''.
  if (trim(current.weight) !== trim(initial.weight)) {
    const w = trim(current.weight)
    if (w !== '') {
      const n = parseInt(w, 10)
      if (!Number.isNaN(n)) {
        payload.weight = n
      }
    }
  }

  // gender_id (diff: only when changed):
  // - real id string ('1'/'2'/'3') → send as int,
  // - the "Unspecified" sentinel → send explicit null (the user chose it),
  // - empty (cleared dropdown) → omit (no clear-to-null path).
  if (trim(current.genderId) !== trim(initial.genderId)) {
    const g = trim(current.genderId)
    if (g === GENDER_UNSPECIFIED) {
      payload.gender_id = null
    } else if (g !== '') {
      const n = parseInt(g, 10)
      if (!Number.isNaN(n)) {
        payload.gender_id = n
      }
    }
  }

  // Address: a nested object. Sent in FULL whenever ANY sub-field changed (the
  // BE replaces the address); ids → int|null. Untouched → omitted entirely.
  const addressChanged = ADDRESS_FIELDS.some(
    (k) => trim(current[k] as string) !== trim(initial[k] as string)
  )
  if (addressChanged) {
    payload.address = {
      street_1: trim(current.addressStreet1),
      street_2: trim(current.addressStreet2),
      city: trim(current.addressCity),
      postal_code: trim(current.addressPostalCode),
      country_id: toIntOrNull(current.addressCountryId),
      province_id: toIntOrNull(current.addressProvinceId),
    }
  }

  return payload
}
